import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    MenuItem,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {HOVER_BOX} from '../../../../shared-components/InfoHover';
import {SMALL_BLUE_BUTTON_SX} from '../SettingsPills';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import DescriptionSettings from './DescriptionSettings';
import SchoolStockDialog from './SchoolStockDialog';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';
import useNotification from '../../../../utility/hooks/useNotification';
import {
    useAddMedicineLookupMutation,
    useDeleteMedicineLookupMutation,
    useGetDosageUnitsQuery,
    useGetMedicineLookupsQuery,
    useReorderMedicineLookupsMutation,
    useUpdateMedicineLookupMutation
} from '../../../../api/services/medicinesService';
import {countInUnit, matchesLookupSearch, MEDICINE_NAME_MAX, unitName} from '../../../../utility/medicines';

const emptyMedicine = {id: null, description: '', dosageUnit: '', brands: []};
// Maria, 24 October 2026: every medicine on the list has a dosage unit, apart from creams and ointments
const needsUnit = medicine => Boolean(medicine) && !medicine.dosageUnit && !/cream|ointment/i.test(medicine.description ?? '');

// The school's medicine list on the Lookups page. Each medicine has its name, its usual dosage unit and its brands,
// one per strength, each with their own unit. The Medicine at School form fills in the unit when a parent selects
// the medicine; the parent types the amount from the box (Maria, 24 October 2026).
const MedicineListSettings = ({sectionDescription, onSectionDescriptionChange}) => {
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {data: medicines = [], isLoading, refetch} = useGetMedicineLookupsQuery();
    const {data: units = []} = useGetDosageUnitsQuery();
    const [addMedicine] = useAddMedicineLookupMutation();
    const [updateMedicine] = useUpdateMedicineLookupMutation();
    const [deleteMedicine] = useDeleteMedicineLookupMutation();
    const [reorderMedicines] = useReorderMedicineLookupsMutation();

    const [editing, setEditing] = useState(null);
    // AD-2715: the medicine whose school stock box is open
    const [stockOf, setStockOf] = useState(null);
    const [search, setSearch] = useState('');
    const [ticking, setTicking] = useState({});
    // Maria, 24 October 2026: one list in the school's order (alphabetical to start with), dragged into a new order;
    // no page arrows, and no column or filter for the children holding a medicine, which is what the filters are for
    const [activeFilter, setActiveFilter] = useState('all');
    const [order, setOrder] = useState(null);
    useEffect(() => setOrder(null), [medicines]);
    const ordered = order ? order.map(id => medicines.find(m => m.id === id)).filter(Boolean) : medicines;
    const shown = ordered
        .filter(medicine => matchesLookupSearch(medicine, search))
        .filter(medicine => activeFilter === 'all' || (activeFilter === 'active') === (medicine.isActive !== false));
    // the whole list is shown while dragging, so a medicine cannot be moved among only some of them
    const canDrag = !search && activeFilter === 'all';
    const [introOpen, setIntroOpen] = useState(false);

    // The API returns a refusal as validationErrors, for example a name already on the list.
    const failureMessage = (error) => error?.data?.validationErrors?.[0]?.errorMessage ??
        error?.data?.title ??
        'That could not be saved';

    const handleDragEnd = async (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const ids = ordered.map(m => m.id);
        const [moved] = ids.splice(result.source.index, 1);
        ids.splice(result.destination.index, 0, moved);
        setOrder(ids);
        try {
            await reorderMedicines(ids).unwrap();
            await refetch();
        } catch (error) {
            setOrder(null);
            showErrorNotification(failureMessage(error));
        }
    };

    const handleSave = async () => {
        const body = {
            description: editing.description,
            dosageUnit: editing.dosageUnit || null,
            packAmount: null,
            brands: editing.brands
                .filter(brand => brand.name.trim())
                .map(brand => ({
                    name: brand.name,
                    // The name before this edit, so every child holding a renamed brand is renamed too.
                    previousName: brand.previousName ?? null,
                    dosageUnit: brand.dosageUnit || null,
                    packAmount: null
                }))
        };

        try {
            if (editing.id) {
                await updateMedicine({id: editing.id, ...body}).unwrap();
                showSuccessNotification(`${editing.description} saved`);
            } else {
                await addMedicine(body).unwrap();
                showSuccessNotification(`${editing.description} added to the list`);
            }
            await refetch();
            setEditing(null);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        }
    };

    // Unticked, a medicine stays on the list, but the parent's form and the Medical tab no longer offer it.
    const handleActive = async (medicine, isActive) => {
        setTicking(current => ({...current, [medicine.id]: isActive}));
        try {
            await updateMedicine({
                id: medicine.id,
                description: medicine.description,
                dosageUnit: medicine.dosageUnit ?? null,
                packAmount: null,
                brands: medicine.brands ?? [],
                isActive
            }).unwrap();
            await refetch();
            showSuccessNotification(isActive
                ? `${medicine.description} is active`
                : `${medicine.description} is inactive. It stays on the list, and forms and profiles no longer offer it`);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        } finally {
            setTicking(({[medicine.id]: _done, ...rest}) => rest);
        }
    };

    const handleDelete = async (medicine) => {
        try {
            await deleteMedicine(medicine.id).unwrap();
            await refetch();
            showSuccessNotification(`${medicine.description} removed from the list`);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        }
    };

    const setBrand = (index, changes) => setEditing({
        ...editing,
        brands: editing.brands.map((brand, i) => (i === index ? {...brand, ...changes} : brand))
    });

    const unitSelect = (value, onChange, testId) => (
        <TextField
            select
            fullWidth
            size="small"
            label="Dosage unit"
            value={value ?? ''}
            inputProps={{'data-testid': testId}}
            onChange={(event) => onChange(event.target.value)}
        >
            <MenuItem value="">None</MenuItem>
            {units.map(unit => <MenuItem key={unit.key} value={unit.key}>{unitName(unit.key, units)}</MenuItem>)}
        </TextField>
    );

    const openEdit = medicine => setEditing({
        id: medicine.id,
        description: medicine.description,
        dosageUnit: medicine.dosageUnit ?? '',
        brands: (medicine.brands ?? []).map(brand => ({
            name: brand.name,
            previousName: brand.name,
            dosageUnit: brand.dosageUnit ?? ''
        }))
    });

    const tableHead = (
        <TableHead>
            <TableRow sx={{'& th': {backgroundColor: '#e3f5f1', fontWeight: 600, py: 0.5, whiteSpace: 'nowrap'}}}>
                <TableCell sx={{width: 32, px: 0.5}}/>
                <TableCell>Medicine</TableCell>
                <TableCell>Dosage unit</TableCell>
                <TableCell>Brands</TableCell>
                <TableCell>School stock</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
            </TableRow>
        </TableHead>
    );

    // Maria, 20 October 2026: the list as a neat table, one row per medicine, in the style of the other School
    // Settings tables. The brands are counted on the row and listed when the pointer is over the number.
    return (
        <Box sx={{width: '100%'}} data-testid="medicine-list-settings">
            <Stack direction={{xs: 'column', md: 'row'}} alignItems={{md: 'center'}} justifyContent="space-between" gap={1} sx={{mb: 1}}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {[
                        ['Active', activeFilter, setActiveFilter, [['all', 'All'], ['active', 'Active'], ['inactive', 'Not active']], 'medicine-filter-active']
                    ].map(([label, value, setValue, options, testId]) => (
                        <TextField
                            key={label}
                            select
                            size="small"
                            label={label}
                            value={value}
                            onChange={event => setValue(event.target.value)}
                            SelectProps={{SelectDisplayProps: {'data-testid': testId}}}
                            sx={{width: 150, '& .MuiInputBase-root': {height: 32, backgroundColor: '#fff', fontSize: 14}}}
                        >
                            {options.map(([key, name]) => <MenuItem key={key} value={key}>{name}</MenuItem>)}
                        </TextField>
                    ))}
                    <TextField
                        size="small"
                        placeholder="Search medicines and brands"
                        value={search}
                        inputProps={{'data-testid': 'medicine-list-search'}}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{width: 260, '& .MuiInputBase-root': {height: 32, backgroundColor: '#fff', fontSize: 14}}}
                    />
                    {/* the text at the top of Medicine at School on the student profile, in a box opened from here */}
                    <Tooltip
                        arrow
                        componentsProps={HOVER_BOX}
                        title="This text appears at the top of the Medicine at School section on the Medical tab of the student profile."
                    >
                        <Button
                            sx={{height: 32, py: 0, px: 1.25, fontSize: 12, backgroundColor: '#fff'}}
                            variant="outlined"
                            data-testid="medicine-intro-text"
                            onClick={() => setIntroOpen(true)}
                        >
                            Introductory text
                        </Button>
                    </Tooltip>
                    <Button
                        sx={SMALL_BLUE_BUTTON_SX}
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon/>}
                        data-testid="add-medicine-to-list"
                        onClick={() => setEditing({...emptyMedicine, brands: []})}
                    >
                        Add new
                    </Button>
                </Stack>
            </Stack>
            <AdmicityDialog
                handleClose={() => setIntroOpen(false)}
                title="Introductory text"
                open={introOpen}
                maxWidth="md"
                actions={[{label: 'Close', onClick: () => setIntroOpen(false)}]}
            >
                <DescriptionSettings
                    fieldLabel={null}
                    rows={4}
                    description={sectionDescription}
                    defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicineAtSchool]}
                    onDescriptionChange={onSectionDescriptionChange}
                />
            </AdmicityDialog>

            <Box sx={{backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, overflow: 'hidden'}} data-testid="medicine-list-scroll">
                {/* AD-2744: the column headings and grey rows show while the list loads, so the page is never blank */}
                {isLoading && (
                    <Table size="small" data-testid="medicine-list-loading">
                        {tableHead}
                        <TableBody>
                            {Array.from({length: 8}, (_, row) => (
                                <TableRow key={row}>
                                    <TableCell sx={{width: 32, px: 0.5}}/>
                                    {Array.from({length: 6}, (__, cell) => (
                                        <TableCell key={cell}><Skeleton variant="text"/></TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {!isLoading && medicines.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                        No medicines are on the list.
                    </Typography>
                )}
                {medicines.length > 0 && shown.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{p: 2}} data-testid="medicine-list-no-match">
                        No medicine on the list matches the search and filters.
                    </Typography>
                )}
                {shown.length > 0 && (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Table size="small" stickyHeader>
                            {tableHead}
                            <Droppable droppableId="medicine-list" isDropDisabled={!canDrag}>
                                {droppable => (
                                    <TableBody ref={droppable.innerRef} {...droppable.droppableProps}>
                                        {shown.map((medicine, index) => {
                                            const brands = medicine.brands ?? [];
                                            return (
                                                <Draggable key={medicine.id} draggableId={String(medicine.id)} index={index} isDragDisabled={!canDrag}>
                                                    {(draggable, snapshot) => (
                                                        <TableRow
                                                            ref={draggable.innerRef}
                                                            {...draggable.draggableProps}
                                                            data-testid={`medicine-lookup-${medicine.id}`}
                                                            sx={{
                                                                // Maria, 20 October 2026: the rows closer together
                                                                height: 32,
                                                                display: snapshot.isDragging ? 'table' : undefined,
                                                                '& td': {
                                                                    backgroundColor: snapshot.isDragging ? '#e3f5f1' : (index % 2 === 1 ? '#f1faf7' : '#fff'),
                                                                    py: 0,
                                                                    whiteSpace: 'nowrap'
                                                                },
                                                                opacity: medicine.isActive === false ? 0.6 : 1
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{width: 32, px: 0.5}}
                                                                title={canDrag ? 'Drag to change the order' : 'Clear the search and filter to change the order'}
                                                                {...draggable.dragHandleProps}
                                                            >
                                                                <DragIndicatorIcon
                                                                    fontSize="small"
                                                                    sx={{display: 'block', color: canDrag ? '#9ca3af' : '#e3e8ef', cursor: canDrag ? 'grab' : 'default'}}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{width: '40%'}}>{medicine.description}</TableCell>
                                                            <TableCell>{medicine.dosageUnit ? unitName(medicine.dosageUnit, units) : '-'}</TableCell>
                                                            <TableCell>
                                                                {brands.length === 0
                                                                    ? '-'
                                                                    : (
                                                                        <Tooltip
                                                                            arrow
                                                                            placement="top"
                                                                            componentsProps={HOVER_BOX}
                                                                            title={brands.map(brand => <Box key={brand.name}>{brand.name}</Box>)}
                                                                        >
                                                                            <Box component="span" sx={{color: '#186BBE', cursor: 'help', borderBottom: '1px dotted #186BBE'}}>
                                                                                {brands.length === 1 ? '1 brand' : `${brands.length} brands`}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box
                                                                    component="button"
                                                                    type="button"
                                                                    data-testid={`medicine-lookup-${medicine.id}-school-stock`}
                                                                    onClick={() => setStockOf(medicine)}
                                                                    sx={{border: 0, background: 'none', p: 0, font: 'inherit', color: '#186BBE', cursor: 'pointer', borderBottom: '1px dotted #186BBE'}}
                                                                >
                                                                    {medicine.schoolStock == null ? '-' : countInUnit(medicine.schoolStock, medicine.dosageUnit, units)}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    sx={{p: 0.25}}
                                                                    checked={ticking[medicine.id] ?? medicine.isActive !== false}
                                                                    disabled={medicine.id in ticking}
                                                                    inputProps={{'data-testid': `medicine-lookup-${medicine.id}-active`, 'aria-label': `${medicine.description} active`}}
                                                                    onChange={(event) => handleActive(medicine, event.target.checked)}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip title="Edit">
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{p: 0.5}}
                                                                        data-testid={`medicine-lookup-${medicine.id}-edit`}
                                                                        onClick={() => openEdit(medicine)}
                                                                    >
                                                                        <EditIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip
                                                                    title={medicine.studentsHolding > 0
                                                                        ? "Remove it from each child's Medicine at School first"
                                                                        : 'Remove from the list'}
                                                                >
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            sx={{p: 0.5}}
                                                                            data-testid={`medicine-lookup-${medicine.id}-delete`}
                                                                            onClick={() => handleDelete(medicine)}
                                                                        >
                                                                            <DeleteIcon fontSize="small"/>
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {droppable.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                )}
            </Box>

            {stockOf && (
                <SchoolStockDialog
                    medicine={stockOf}
                    units={units}
                    onClose={() => setStockOf(null)}
                    onChanged={refetch}
                />
            )}

            <AdmicityDialog
                open={Boolean(editing)}
                title={editing?.id ? 'Edit medicine' : 'New medicine'}
                maxWidth="md"
                handleClose={() => setEditing(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setEditing(null)},
                    {label: 'Save', onClick: handleSave, disabled: !editing?.description?.trim() || needsUnit(editing)}
                ]}
            >
                {editing && (
                    <Stack gap={2} sx={{mt: 1}}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Medicine name"
                            data-testid="medicine-description"
                            inputProps={{maxLength: MEDICINE_NAME_MAX}}
                            value={editing.description}
                            onChange={(event) => setEditing({...editing, description: event.target.value})}
                        />
                        {unitSelect(editing.dosageUnit, value => setEditing({...editing, dosageUnit: value}), 'medicine-unit')}

                        <Typography variant="subtitle2">Brands, one for each strength</Typography>
                        {editing.brands.map((brand, index) => (
                            <Stack
                                key={index}
                                direction={{xs: 'column', sm: 'row'}}
                                gap={1}
                                alignItems={{sm: 'center'}}
                                data-testid={`medicine-brand-${index}`}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Brand name and strength"
                                    value={brand.name}
                                    inputProps={{maxLength: MEDICINE_NAME_MAX, 'data-testid': `medicine-brand-name-${index}`}}
                                    onChange={(event) => setBrand(index, {name: event.target.value})}
                                />
                                {unitSelect(brand.dosageUnit, value => setBrand(index, {dosageUnit: value}), `medicine-brand-unit-${index}`)}
                                <IconButton
                                    aria-label="Remove brand"
                                    data-testid={`medicine-brand-remove-${index}`}
                                    onClick={() => setEditing({
                                        ...editing,
                                        brands: editing.brands.filter((_, i) => i !== index)
                                    })}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Stack>
                        ))}
                        <Box>
                            <Button
                                size="small"
                                startIcon={<AddCircleOutlineIcon/>}
                                data-testid="medicine-add-brand"
                                onClick={() => setEditing({
                                    ...editing,
                                    brands: [...editing.brands, {name: '', dosageUnit: editing.dosageUnit}]
                                })}
                            >
                                Add a brand
                            </Button>
                        </Box>
                    </Stack>
                )}
            </AdmicityDialog>
        </Box>
    );
};

MedicineListSettings.propTypes = {
    sectionDescription: PropTypes.string,
    onSectionDescriptionChange: PropTypes.func
};

export default MedicineListSettings;
